import { FreelancerSearchResult } from '../types/search';
import { FreelancerProfile } from '../types/profile';
import { UserAlliance, ActiveAlliance } from '../types/alliance';
import { QuoteTask } from '../types/quote';

export function getFreelancerSubtitle(
  freelancer: { job_title?: string | null; service_category_slugs?: string[] },
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (freelancer.job_title) {
    return freelancer.job_title;
  }

  if (!freelancer.service_category_slugs?.length) {
    return '';
  }

  return freelancer.service_category_slugs
    .map(slug => {
      const label = slug.replace(/-/g, ' ');
      const formattedLabel = label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();

      return t(`serviceCategories.${slug}`, {
        ns: 'common',
        defaultValue: formattedLabel,
      });
    })
    .join(', ');
}

export const getServices = (freelancer: { service_category_slugs?: string[] }): string => {
  if (freelancer.service_category_slugs && freelancer.service_category_slugs.length > 0) {
    const firstService = freelancer.service_category_slugs[0];

    if (freelancer.service_category_slugs.length > 1) {
      return `${firstService}...`;
    }

    return firstService;
  }

  return '';
};

export function mapProfileToFreelancerSearchResult(
  profile: FreelancerProfile
): FreelancerSearchResult {
  return {
    id: profile.id,
    first_name: profile.first_name,
    last_name: profile.last_name ?? null,
    job_title: profile.job_title ?? null,
    service_category_slugs: profile.services
      ? profile.services.map((s: { slug: string }) => s.slug)
      : [],
    avatar_url: profile.avatar_url ?? null,
    bio: profile.bio ?? null,
    alliance_count: profile.stats?.alliance_count ?? 0,
    recent_alliances: profile.recent_alliances ?? [],
    full_access: profile.full_access,
    alliance_status: profile.alliance_status,
    overall_rating: profile.stats?.overall_rating ?? 0,
    reviews_count: profile.stats?.reviews_count ?? 0,
    projects_count: profile.stats?.projects_count ?? 0,
  };
}

export const mapActiveAllianceToSearchResult = (
  alliance: ActiveAlliance
): FreelancerSearchResult => {
  return {
    id: alliance.profile_id,
    first_name: alliance.profile_first_name,
    last_name: alliance.profile_last_name,
    job_title: alliance.profile_job_title,
    service_category_slugs: alliance.highlighted_service_category
      ? [alliance.highlighted_service_category.slug]
      : alliance.service_categories.map(cat => cat.slug),
    avatar_url: alliance.profile_avatar_url,
    bio: alliance.profile_bio,
    alliance_count: alliance.alliance_count,
    recent_alliances: alliance.recent_alliances.map(alliance => ({
      profile_id: alliance.profile_id,
      first_name: alliance.first_name,
      avatar_url: alliance.avatar_url,
    })),
    full_access: alliance.full_access,
    alliance_status: alliance.alliance_status,
    overall_rating: alliance.profile_stats.overall_rating,
    reviews_count: alliance.profile_stats.reviews_count,
    projects_count: alliance.profile_stats.projects_count,
  };
};

export const mapUserAllianceToSearchResult = (
  userAlliance: UserAlliance
): FreelancerSearchResult => {
  return {
    id: userAlliance.user.id,
    first_name: userAlliance.user.first_name,
    last_name: userAlliance.user.last_name ?? null,
    job_title: null,
    service_category_slugs: userAlliance.user.service_category_slugs,
    avatar_url: userAlliance.user.avatar_url,
    bio: userAlliance.user.bio,
    alliance_count: userAlliance.user.alliance_count,
    recent_alliances: userAlliance.user.recent_alliances,
    full_access: userAlliance.user.full_access,
    alliance_status: userAlliance.status,
    // Alliance listings carry no review stats; 0 matches how the UI already treated the missing values.
    overall_rating: 0,
    reviews_count: 0,
    projects_count: 0,
  };
};

export function getMaxTotalAmount(tasks: QuoteTask[]): number {
  if (tasks.length === 0) return 0; // ou throw new Error("Le tableau est vide") selon vos besoins
  return Math.max(...tasks.map(task => task.total_amount));
}
